import React, { useState, useEffect, useMemo } from 'react';
import { TextField, Autocomplete, CircularProgress } from '@mui/material';
import debounce from 'lodash/debounce';

interface AsyncSearchTextBoxProps<T> {
    disabled?: boolean;
    value: T | null;
    onChange: (value: T | null) => void;
    fetchOptions: (query: string) => Promise<T[]>;
    getOptionLabel: (option: T) => string;
    isOptionEqualToValue: (option: T, value: T) => boolean;
}

export default function AsyncSearchTextBox<T>({ disabled = false, value, onChange, fetchOptions, getOptionLabel, isOptionEqualToValue, }: AsyncSearchTextBoxProps<T>) {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<readonly T[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [loading, setLoading] = useState(false);

    // 1. Debounced API call
    const debouncedFetch = useMemo(
        () =>
            debounce(async (query: string) => {
                setLoading(true);
                try {
                    const results = await fetchOptions(query);
                    // Filter out the currently selected value if it's in the API results
                    const filteredResults = results.filter(result => !value || !isOptionEqualToValue(result, value));
                    // Merge [current value] + [remaining unique results]
                    setOptions(value ? [value, ...filteredResults] : filteredResults);
                } catch (error) {
                    console.error("Search error:", error);
                } finally {
                    setLoading(false);
                }
            }, 500),
        [fetchOptions, isOptionEqualToValue, value]
    );

    // 2. Fetch effect
    useEffect(() => {
        let active = true;

        if (inputValue === '') {
            setOptions(value ? [value] : []);
            return;
        }

        if (active) debouncedFetch(inputValue);

        return () => { active = false; };
    }, [inputValue, debouncedFetch, value]);

    return (
        <Autocomplete<T, false, false, false>
            ChipProps={{ color: "info" }} size="small"
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            options={options}
            loading={loading}
            value={value}
            onChange={(_e, newValue) => onChange(newValue)}
            onInputChange={(_e, newInputValue) => setInputValue(newInputValue)}
            filterOptions={(x) => x} // API handles filtering
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined" color="primary"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
        />
    );
}
